// @dynamic-i18n-keys
// Dynamic t() at line 230 (hasCoreMembership, isDesktop):
//   uploadZone.ready.subtitle
//   uploadZone.ready.subtitle.core
//   uploadZone.ready.subtitle.mobile
//   uploadZone.ready.subtitle.core.mobile
import React, {
  useCallback,
  useState,
  useEffect,
  useRef,
  useMemo,
  useContext,
} from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import Modal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import SelectField from '@wix/da-ds/pkg/Dropdown/select/SelectField';
import UploadItemButton from '@wix/da-shared-react/pkg/UploadItemButton';
import NativeDropdown from '@wix/da-ds/pkg/Dropdown/NativeDropdown';
import SelectToggle from '@wix/da-shared-react/pkg/SelectToggle';
import { MIME_CONFIG } from '@wix/da-shared-react/pkg/utils/mime';
import { UploadType } from '@wix/da-shared-react/pkg/redux/uploads/types';
import { useUnlockVideoUploadsCheck } from '../UnlockWithCoreUpsellModal/useUnlockVideoUploadsCheck';
import {
  GenericRequestStatusType,
  PresetsInitResponse,
} from '../../redux/types';
import FileTypeButton from './_partials/FileTypeButton';
import { navigateToSanitizedUrl } from '@wix/da-url';
import { SAVED_SUBMISSIONS_FOLDER_NAME } from '../../constants';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks/useFlag';

import s from './DeviationPopupDropZone.scss';

const STUDIO_UPLOADS_NAMESPACE = 'studio';

export interface Props {
  className?: string;
  tierId?: number;
  referer?: string;
  isPageLoadModal?: boolean;
  requestClose: () => void;
  initializeTierTags: (tierId: number, onError: () => void) => void;
  onDeviationCreated: (deviationId: number) => void;
  presetsStatus: GenericRequestStatusType;
  presetsInit?: PresetsInitResponse;
  presetsInitFetch: () => void;
  presetsSetActive: (presetId: number) => void;
}

const DeviationPopupDropZone: React.FC<Props> = ({
  className,
  tierId,
  referer,
  isPageLoadModal,
  requestClose,
  initializeTierTags,
  onDeviationCreated,
  presetsStatus,
  presetsInit,
  presetsInitFetch,
  presetsSetActive,
}) => {
  const isDesktop = !useContext(MobileContext);
  const { hasCoreMembership } = useContext(SessionContext);
  const presetsReady =
    presetsStatus !== 'uninitialized' && presetsInit !== undefined;

  const [dragOver, setDragOver] = useState(false);
  const isMultiUploadRef = useRef<boolean>(false);
  const { t } = useTranslation();

  const onSuccess = useCallback(
    ({ deviationId }) => {
      if (!isMultiUploadRef.current) {
        onDeviationCreated(deviationId);
      }
    },
    [onDeviationCreated]
  );

  const onDragChange = useCallback(isDragging => setDragOver(isDragging), []);

  const {
    modal: lockedVideoUpsellDialog,
    showModal: showLockedVideoUpsellModal,
    areVideoUploadsLocked,
    ...lockedVideoUploadOptions
  } = useUnlockVideoUploadsCheck();
  const onDrop = useCallback(
    files => {
      // If multiple files are dropped we close the modal to reveal studio page below
      if (files.length > 1) {
        isMultiUploadRef.current = true;
        requestClose();
      }
      return true;
    },
    [requestClose]
  );

  // Determine if we should redirect back to the referer when the modal is closed
  const redirectOnClose = useMemo(() => {
    const url =
      typeof window !== 'undefined' && new URL(window.location.toString());
    const stopRedirectToReferer =
      url &&
      url.searchParams &&
      url.searchParams.size === 1 &&
      url.searchParams.get('new');

    return isPageLoadModal && !stopRedirectToReferer;
  }, [isPageLoadModal]);

  const onModalClose = useCallback(() => {
    if (redirectOnClose && referer) {
      navigateToSanitizedUrl(referer);
    } else {
      requestClose();
    }
  }, [redirectOnClose, referer, requestClose]);

  useEffect(() => {
    if (tierId) {
      initializeTierTags(tierId, () => {
        requestClose(); // on error
      });
    }
  }, [tierId, initializeTierTags, requestClose]);

  const [presetsIsOpen, setPresetsIsOpen] = useState<boolean>(false);
  const onSelectPreset = useCallback(
    presetId => {
      presetsSetActive(presetId);
      setPresetsIsOpen(false);
    },
    [presetsSetActive, setPresetsIsOpen]
  );

  useEffect(() => {
    if (!presetsReady) {
      presetsInitFetch();
    }
  }, [presetsReady, presetsInitFetch]);

  const commonUploadOptions = useMemo(
    () => ({
      id: STUDIO_UPLOADS_NAMESPACE,
      uploadType: UploadType.DEVIATION_NEW,
      uploadParams: {
        folderName: SAVED_SUBMISSIONS_FOLDER_NAME,
        useDefaults: true,
      },
      allowMultiple: true,
      onDrop,
      ...lockedVideoUploadOptions,
    }),
    [onDrop, lockedVideoUploadOptions]
  );

  let presetsSelector;
  if (presetsReady && presetsInit?.presets.length >= 2) {
    presetsSelector = isDesktop ? (
      <div className={s['presets']}>
        <span className={s['presets-label']}>
          {t('deviationSubmit.modals.presets.title')}
        </span>
        <SelectField
          className={s['presets-select']}
          id="presetId"
          label={t('deviationSubmit.modals.presets.title')}
          isLabelHidden
          noBottomSpacing
          withBlankOption={false}
          value={presetsInit?.presetId}
          items={presetsInit?.presets.map(preset => ({
            value: preset.presetId,
            label: preset.presetName || 'Untitled',
          }))}
          onSelect={onSelectPreset}
        />
      </div>
    ) : (
      <div className={s['presets']}>
        <SelectToggle
          label={t('deviationSubmit.modals.presets.title')}
          value={presetsInit?.presetName || 'Untitled'}
          isToggled={presetsIsOpen}
        />
        <div className={s['presets-dropdown-hidden']}>
          <NativeDropdown
            value={presetsInit?.presetId}
            items={presetsInit?.presets.map(preset => ({
              value: preset.presetId,
              label: preset.presetName || 'Untitled',
            }))}
            onClick={() => setPresetsIsOpen(!presetsIsOpen)}
            onSelect={onSelectPreset}
          />
        </div>
      </div>
    );
  }

  const isMaintenanceMode = useFlag('disable_wixmp_uploads');

  if (isMaintenanceMode) {
    return (
      <Modal
        isOpen
        isFullscreen
        heading={t(
          tierId
            ? 'deviationSubmit.title.createToTier'
            : 'deviationSubmit.title.create'
        )}
        onRequestClose={onModalClose}
        layoutClassName={s['layout']}
        showHeaderBorder
      >
        <div className={s['maintenance-mode']}>
          <img src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAHgAAACiCAYAAACKwQmbAAAACXBIWXMAABYlAAAWJQFJUiTwAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAOdEVYdFNvZnR3YXJlAEZpZ21hnrGWYwAAA5lJREFUeAHt3cFLFGEYx/F3UxG7aB3KUKxIRNhcw1KIsOjsJYSCYohOCZ2DIAg8Sf0BEQYhkXVI6FZ4ClK6aIhmXVYpjIQ6hAmBZW3rTfbwvIeBd3dmf/v9HJ+GIfwy8PgyuzoHAAAAAAAAAAAAAAAAAAgl47BrdipTdAEMXiym5ue6x0EagcURWByBxRFYXE1u0b5t+cRAvXn94tw/c35maMycv31525wnsV3zBIsjsDgCiyOwOAKLq3fYVQxyEp0uPMHiCCyOwOIILI7A4qTPon1nzr39yfzysDRvn2mX84yaJ1gcgcURWByBxRFYXE2+0THU1WRu13ejZvP6gz0N5vz78l9zfmty05y/ym/xRgfCIrA4AosjsDgCi+ONjhJjUz/N+f2+A7GuT9MvJzzB4ggsjsDiCCyOwOLYokuMjx835yMjH8z5xPQ1cz58/rFLC55gcQQWR2BxBBZHYHFs0SWe3Fsw5xPT18359to7l3Y8weIILI7A4ggsjsDi2KJLdBxuM+ePbjw051eu/ne2JpcWPMHiCCyOwOIILI7A4mry04Vzw43mpwvnC/anBc+etN+Lbs9+M+crTxvN+cCLP3y6EGERWByBxRFYHIHFpfIs+lJfs7nl3jzy24XQO1mw/yFqcHH4tmXf/ecie3sv53bNEyyOwOIILI7A4ggsLtEtOu623Ne57UIouLpY919Y3Ih1ve/+SeAJFkdgcQQWR2BxBBZXkS3aty3fuWC/P7y1aG/RhdHybqfe+0cxr08RnmBxBBZHYHEEFkdgcYmeRc98ajHn/c4++60b9byJUXb2thz//1P5rZsnWByBxRFYHIHFEVhcRbbo5wub5nu/RVc0z6hd7qg5dqufzbH3PedQQp1FR67ieILFEVgcgcURWByBxSV6Fp3xfUXIRt4cz+/rsq+P7O06lO7LPeZ8KVp2cfAdHQiOwOIILI7A4ggsLpXfdJc71maeUWcbt8zrW9vs73le//HFnJ869MucP8u3mvOR7Lo5n8nvN+cft+33vd+vrrNFIywCiyOwOAKLI7C4qvqrK3tPd5vz17OebXkwa84n36yZc9+W+6DYbm71/ec6nMlz/yTwBIsjsDgCiyOwOAKLq6otuqWlvNfHVe77h8ATLI7A4ggsjsDiCCyuqv52Ya7TftMjFN9ZdK7TPovOxPzpLa185Y0OhEVgcQQWR2BxBAYAAAAAAAAAAAAAAAAAAEAN2AEfMqLt6g8+sAAAAABJRU5ErkJggg==" />
          <h2>Temporary Maintenance Mode</h2>
          <p>
            Uploads are undergoing scheduled maintenance to improve your
            experience.
          </p>
          <p>Please check back shortly.</p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen
      isFullscreen
      heading={t(
        tierId
          ? 'deviationSubmit.title.createToTier'
          : 'deviationSubmit.title.create'
      )}
      headerRight={isDesktop && presetsSelector}
      subheading={!isDesktop && presetsSelector}
      layoutClassName={s['layout']}
      showHeaderBorder
      onRequestClose={onModalClose}
    >
      <div
        className={classnames(s['root'], className, dragOver && s['drag-over'])}
      >
        {lockedVideoUpsellDialog}
        <UploadItemButton
          uploadOptions={{
            ...commonUploadOptions,
            acceptedMimeTypes: MIME_CONFIG.forDeviationSubmit,
          }}
          className={s['upload-button']}
          dropZoneClassName={s['upload-dropzone']}
          ctaLabel={t('uploadZone.ready.cta')}
          subtitle={t(
            `uploadZone.ready.subtitle${hasCoreMembership ? '.core' : ''}${
              isDesktop ? '' : '.mobile'
            }`
          )}
          onSuccess={onSuccess}
          onDragChange={onDragChange}
          aboveCtaSlot={
            <div className={s['file-type-buttons']}>
              <FileTypeButton
                fileType="images"
                uploadOptions={commonUploadOptions}
                onSuccess={onSuccess}
              />
              <FileTypeButton
                fileType="videos"
                uploadOptions={commonUploadOptions}
                onSuccess={onSuccess}
                onLockedClick={
                  areVideoUploadsLocked ? showLockedVideoUpsellModal : undefined
                }
              />
              <FileTypeButton
                fileType="gifs"
                uploadOptions={commonUploadOptions}
                onSuccess={onSuccess}
              />
            </div>
          }
          belowCtaSlot={
            <TextButton
              className={s['studio-link']}
              size="large"
              variant="main"
              onClick={ev => {
                ev.stopPropagation();
                requestClose();
              }}
            >
              {t('uploadZone.ready.studioLink')}
            </TextButton>
          }
        />
      </div>
    </Modal>
  );
};
DeviationPopupDropZone.displayName = 'DeviationPopupDropZone';

export default DeviationPopupDropZone;
